import { useEffect, useState } from "react";
import { isProduction } from "../config/environment";
import { decodeTokenService } from "../services/decodeTokenService";
import { userExperimentsService } from "../services/userExperiments/userExperimentsService";
import { isLocalHostname } from "../utils/localHost";

const CHATBOT_V2_FLAG = "chatbot_v2";

const cachedChatbotEnabledByOwner: Record<string, boolean> = {};
const inFlightRequestByOwner: Record<string, Promise<boolean> | undefined> = {};

const isLocalChatbotEnabled = (): boolean => {
  if (!import.meta.env.DEV) return false;
  if (!import.meta.env.VITE_LOCAL_OWNER_ID) return false;

  return isLocalHostname(window.location.hostname);
};

const isNonProductionChatbotEnabled = (): boolean => !isProduction;

const isEnabledValue = (value: unknown): boolean => {
  if (value === true) return true;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return false;
};

const loadChatbotV2Flag = async (ownerId: string): Promise<boolean> => {
  if (cachedChatbotEnabledByOwner[ownerId] !== undefined) {
    return cachedChatbotEnabledByOwner[ownerId];
  }

  if (!inFlightRequestByOwner[ownerId]) {
    inFlightRequestByOwner[ownerId] = userExperimentsService
      .getExperiments()
      .then((response) => {
        const enabled = isEnabledValue(response.experiments?.[CHATBOT_V2_FLAG]?.enabled);
        cachedChatbotEnabledByOwner[ownerId] = enabled;
        return enabled;
      })
      .catch((error) => {
        console.error("Error fetching chatbot_v2 flag:", error);
        cachedChatbotEnabledByOwner[ownerId] = false;
        return false;
      })
      .finally(() => {
        inFlightRequestByOwner[ownerId] = undefined;
      });
  }

  return inFlightRequestByOwner[ownerId];
};

export const useChatbotV2Flag = () => {
  const ownerId = decodeTokenService.getUserIdFromToken();
  const chatbotEnabledByEnvironment = isNonProductionChatbotEnabled() || isLocalChatbotEnabled();
  const [enabled, setEnabled] = useState(() =>
    chatbotEnabledByEnvironment || (ownerId ? cachedChatbotEnabledByOwner[ownerId] === true : false)
  );
  const [loading, setLoading] = useState(() =>
    chatbotEnabledByEnvironment ? false : ownerId ? cachedChatbotEnabledByOwner[ownerId] === undefined : false
  );

  useEffect(() => {
    let active = true;

    if (chatbotEnabledByEnvironment) {
      setEnabled(true);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    if (!ownerId) {
      setEnabled(false);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(cachedChatbotEnabledByOwner[ownerId] === undefined);
    loadChatbotV2Flag(ownerId).then((nextEnabled) => {
      if (!active) return;
      setEnabled(nextEnabled);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [chatbotEnabledByEnvironment, ownerId]);

  return { enabled, loading };
};
